import { Component, OnInit, signal } from '@angular/core';
import { KblayerService } from '../../../services/kblayer-service';
import Universe from '../../../models/KB Layer/universe';
import { FormsModule } from '@angular/forms';
import Campaign from '../../../models/KB Layer/campaign';
import Entity from '../../../models/KB Layer/entity';
import GameMap from '../../../models/KB Layer/map';
import { HttpErrorResponse } from '@angular/common/http';
import { BackButtonComponent } from '../../back-button-component/back-button-component';
import { LoadingComponent } from '../../loading-component/loading-component';

@Component({
  selector: 'app-universe-component',
  imports: [FormsModule, BackButtonComponent, LoadingComponent],
  standalone: true,
  templateUrl: './universe-component.html',
  styleUrl: './universe-component.css',
})
export class UniverseComponent implements OnInit {
  universes = signal<Universe[]>([]);
  isLoadingUniverses = signal(true);
  loadUniversesError = signal('');
  deleteUniverseError = '';
  deletingUniverseId = '';
  updateUniverseError = '';
  editingUniverseId = '';
  editingUniverseDraft = new Universe();
  isUpdatingUniverse = false;
  newUniverse: Universe = new Universe();
  isNewUniverseOpen = false;
  createError = '';
  isCreatingUniverse = false;

  
  constructor(private kbService: KblayerService) { }

  ngOnInit(): void {
    this.getUniverses();
  }

  getUniverses(): void {
    this.isLoadingUniverses.set(true);
    this.loadUniversesError.set('');
    this.deleteUniverseError = '';
    this.updateUniverseError = '';

    this.kbService.getUniverses().subscribe({
      next: (response: { universes: Universe[] }) => {
        this.universes.set((response?.universes ?? []).map((universe) => new Universe(universe)));
        this.isLoadingUniverses.set(false);
      },
      error: (error) => {
        this.isLoadingUniverses.set(false);
        this.loadUniversesError.set('Failed to load universes from the backend.');
        console.error('Error fetching universes:', error);
      }
    });
  }


  addCampaign(): void {
    this.newUniverse.campaigns.push(new Campaign());
  }

  addEditCampaign(): void {
    this.editingUniverseDraft.campaigns.push(new Campaign());
  }

  getVisibleUniverses(): Universe[] {
    return this.universes().filter((universe) => universe.visible !== false);
  }

  getCampaigns(universe?: Universe): Campaign[] {
    return universe?.campaigns ?? [];
  }

  getEntities(universe?: Universe): Entity[] {
    return universe?.entities ?? [];
  }

  getPlayableEntities(universe?: Universe): Entity[] {
    return this.getEntities(universe).filter((entity) => entity.isPlayable);
  }

  getNonPlayableEntities(universe?: Universe): Entity[] {
    return this.getEntities(universe).filter((entity) => !entity.isPlayable);
  }

  getCampaignEntities(campaign?: Campaign): Entity[] {
    return campaign?.entities ?? [];
  }

  getMaps(target?: Universe | Campaign): GameMap[] {
    return target?.maps ?? [];
  }

  getStats(entity?: Entity): any[] {
    return entity?.stats ?? [];
  }

  getEditingUniverse(): Universe {
    return this.editingUniverseDraft;
  }

  addUniverseEntity(isPlayable: boolean): void {
    this.editingUniverseDraft.entities.push(new Entity({ isPlayable, stats: [] }));
  }

  removeUniverseEntity(entity: Entity): void {
    this.editingUniverseDraft.entities = this.editingUniverseDraft.entities.filter((item) => item !== entity);
  }

  addUniverseMap(): void {
    this.editingUniverseDraft.maps.push(new GameMap());
  }

  removeUniverseMap(map: GameMap): void {
    this.editingUniverseDraft.maps = this.editingUniverseDraft.maps.filter((item) => item !== map);
  }

  addCampaignEntity(campaign: Campaign, isPlayable: boolean): void {
    campaign.entities.push(new Entity({ isPlayable, stats: [] }));
  }

  removeCampaignEntity(campaign: Campaign, entity: Entity): void {
    campaign.entities = campaign.entities.filter((item) => item !== entity);
  }

  getPlayableCampaignEntities(campaign?: Campaign): Entity[] {
    return this.getCampaignEntities(campaign).filter((entity) => entity.isPlayable);
  }

  getNonPlayableCampaignEntities(campaign?: Campaign): Entity[] {
    return this.getCampaignEntities(campaign).filter((entity) => !entity.isPlayable);
  }

  addCampaignMap(campaign: Campaign): void {
    campaign.maps.push(new GameMap());
  }

  removeCampaignMap(campaign: Campaign, map: GameMap): void {
    campaign.maps = campaign.maps.filter((item) => item !== map);
  }

  addStat(entity: Entity): void {
    entity.stats.push({ name: '', value: 0 });
  }

  removeStat(entity: Entity, index: number): void {
    entity.stats.splice(index, 1);
  }

  removeCampaign(index: number): void {
    if (this.newUniverse.campaigns.length > 1) {
      this.newUniverse.campaigns.splice(index, 1);
    }
  }

  removeEditCampaign(index: number): void {
    if (this.editingUniverseDraft.campaigns.length > 1) {
      this.editingUniverseDraft.campaigns.splice(index, 1);
    }
  }

  isEditingUniverse(universe: Universe): boolean {
    return this.editingUniverseId === universe.id;
  }

  startEditingUniverse(universe: Universe): void {
    this.updateUniverseError = '';
    this.editingUniverseId = universe.id;
    this.editingUniverseDraft = new Universe(JSON.stringify(universe));
  }

  cancelEditingUniverse(): void {
    this.editingUniverseId = '';
    this.editingUniverseDraft = new Universe();
    this.updateUniverseError = '';
    this.isUpdatingUniverse = false;
  }

  canUpdateUniverse(): boolean {
    return !!this.editingUniverseDraft.name.trim()
      && !!this.editingUniverseDraft.description.trim()
      && this.editingUniverseDraft.campaigns.length > 0
      && this.editingUniverseDraft.campaigns.every((campaign) =>
        !!campaign.name.trim() && !!campaign.description.trim()
      );
  }

  canCreateUniverse(): boolean {
    return !!this.newUniverse.name.trim()
      && !!this.newUniverse.description.trim()
      && this.newUniverse.campaigns.length > 0
      && this.newUniverse.campaigns.every(c =>
        !!c.name.trim() && !!c.description.trim()
      );
  }

  createUniverse(): void {
    if (!this.canCreateUniverse() || this.isCreatingUniverse) {
      return;
    }

    this.createError = '';
    this.isCreatingUniverse = true;

    this.kbService.createUniverse(this.newUniverse).subscribe({
      next: () => {
        this.getUniverses(); // Refresh the list after creation
        this.newUniverse = new Universe();
        this.isNewUniverseOpen = false;
        this.isCreatingUniverse = false;
      },
      error: (error) => {
        this.isCreatingUniverse = false;
        this.createError = this.getCreateUniverseErrorMessage(error);
        console.error('Error creating universe:', error);
      }
    });
  }

  updateUniverse(universe: Universe): void {
    if (!universe.id || !this.isEditingUniverse(universe) || !this.canUpdateUniverse() || this.isUpdatingUniverse) {
      return;
    }

    this.updateUniverseError = '';
    this.isUpdatingUniverse = true;

    this.kbService.updateUniverse(universe.id, this.editingUniverseDraft).subscribe({
      next: () => {
        this.isUpdatingUniverse = false;
        this.cancelEditingUniverse();
        this.getUniverses();
      },
      error: (error) => {
        this.isUpdatingUniverse = false;
        this.updateUniverseError = this.getUpdateUniverseErrorMessage(error);
        console.error('Error updating universe:', error);
      }
    });
  }

  deleteUniverse(universe: Universe): void {
    if (!universe.id || this.deletingUniverseId) {
      return;
    }

    const isConfirmed = window.confirm(`Delete "${universe.name}"? This action cannot be undone.`);
    if (!isConfirmed) {
      return;
    }

    this.deleteUniverseError = '';
    this.deletingUniverseId = universe.id;

    this.kbService.deleteUniverse(universe.id).subscribe({
      next: () => {
        this.deletingUniverseId = '';
        this.getUniverses();
      },
      error: (error) => {
        this.deletingUniverseId = '';
        this.deleteUniverseError = this.getDeleteUniverseErrorMessage(error);
        console.error('Error deleting universe:', error);
      }
    });
  }

  private getCreateUniverseErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 404) {
        return 'The server was reached, but POST /api/kb/universes does not exist yet.';
      }

      if (error.status === 0) {
        return 'Could not reach the backend server at all. Check that it is running and accessible.';
      }

      return `Failed to create universe (${error.status} ${error.statusText}).`;
    }

    return 'Failed to create universe due to an unexpected error.';
  }

  private getDeleteUniverseErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 404) {
        return 'The server was reached, but DELETE /api/kb/universes/:id does not exist yet.';
      }

      if (error.status === 0) {
        return 'Could not reach the backend server to delete the universe.';
      }

      return `Failed to delete universe (${error.status} ${error.statusText}).`;
    }

    return 'Failed to delete universe due to an unexpected error.';
  }

  private getUpdateUniverseErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 404) {
        return 'The server was reached, but PUT /api/kb/universes/:id does not exist yet.';
      }

      if (error.status === 0) {
        return 'Could not reach the backend server to update the universe.';
      }

      return `Failed to update universe (${error.status} ${error.statusText}).`;
    }

    return 'Failed to update universe due to an unexpected error.';
  }

  onNewUniverseToggle(event: Event): void {
    this.isNewUniverseOpen = (event.target as HTMLDetailsElement).open;
  }
}
